import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Input, InputNumber, Tag, Modal, AutoComplete } from 'antd';
import { SearchOutlined, CameraOutlined, PictureOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import { useSelector } from 'react-redux';
import usePermissions from '../../hooks/usePermissions';
import canSeeFinance from '../../utils/canSeeFinance';
import {
  NdoePage, NdoePageHeader, NdoeFormSection, NdoeActionButton,
  NdoeImage, NdoeLoading, NdoeStats, NdoeStatCard, optionsCouleursParFamille,
  NdoeError, chargerReferentiels, chargerListe,
} from '../../components/ndoe';
import {
  CLE_BROUILLON_LEGACY, cleBrouillon, identiteValide, identiteRaison,
  lignesDeLaMatrice, totalPaires, matriceValide, matriceRaison,
  prixValide, prixRaison, construireCorps, recapitulatif,
  brouillonDepuis, lireBrouillon, brouillonUtile, grouperPointures, dateEntreeRaison,
  confirmationSansMarchandiseRequise, systemesPointureMelanges, nouvelleCleCreation,
} from './nouveauProduitLogic';
import './nouveauProduit.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const money = (n) => `${Number(n || 0).toLocaleString('fr-FR')} $`;

// Le serveur en accepte 5 par modèle (photoPartage.service MAX_PHOTOS). On le
// dit ici plutôt que de laisser l'utilisatrice découvrir le refus à la sixième.
const MAX_PHOTOS_PARTAGE = 5;

// GARDE-FOU CONTRE LA FRAPPE, PAS LIMITE DE PRIX (17/08/2026).
// Mesuré en production : 634 modèles de 35 à 115 $, aucun au-dessus de 300.
// « 900 » au lieu de « 90 » partait en vente sans un mot. Le seuil réel est
// réglable dans Paramètres de la boutique (`catalogue_prix_alerte`) ; celui-ci
// ne sert que si le serveur est injoignable.
const SEUIL_PRIX_DEFAUT = 200;

/*
 * PHOTOS DU CATALOGUE DE PARTAGE — glisser-déposer, ou sélection classique.
 *
 * Ce sont les photos « mannequin » que l'on envoie aux clientes sur WhatsApp :
 * autre chose que la photo de la paire au catalogue interne. Elles vivaient
 * uniquement dans l'écran « Photos à partager », donc APRÈS coup — alors qu'on
 * les a sous la main au moment où l'on enregistre l'arrivage.
 *
 * Le dépôt est du glisser-déposer NATIF, sans bibliothèque : c'est un
 * `dataTransfer` avec `preventDefault`, et rien de plus. Sans le
 * `preventDefault`, le navigateur quitte la page pour ouvrir l'image — la
 * saisie en cours serait perdue.
 */
function ZonePhotosPartage({ fichiers, onChanger }) {
  const [survol, setSurvol] = useState(false);
  const [apercus, setApercus] = useState([]);

  // Une URL d'objet par fichier, révoquée à chaque changement : sinon dix essais
  // laissent dix images en mémoire.
  useEffect(() => {
    const urls = fichiers.map((f) => URL.createObjectURL(f));
    setApercus(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [fichiers]);

  const plein = fichiers.length >= MAX_PHOTOS_PARTAGE;

  const ajouter = (liste) => {
    const images = Array.from(liste || []).filter((f) => /^image\//.test(f.type || ''));
    const refuses = Array.from(liste || []).length - images.length;
    if (refuses > 0) toast.info('Seules les images sont acceptées.');
    if (!images.length) return;
    const place = MAX_PHOTOS_PARTAGE - fichiers.length;
    if (images.length > place) {
      toast.info(`${MAX_PHOTOS_PARTAGE} photos de partage au maximum : ${images.length - place} écartée(s).`);
    }
    onChanger([...fichiers, ...images.slice(0, Math.max(0, place))]);
  };

  const contientFichiers = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  return (
    <div
      className={`np-partage ${survol ? 'is-survol' : ''} ${plein ? 'is-plein' : ''}`}
      onDragOver={(e) => {
        if (!contientFichiers(e)) return;
        e.preventDefault();                       // sinon le navigateur ouvre le fichier
        e.dataTransfer.dropEffect = plein ? 'none' : 'copy';
        if (!plein) setSurvol(true);
      }}
      onDragLeave={(e) => {
        // Entrer dans un enfant déclenche un dragleave sur le parent : on ne
        // retire la surbrillance que si l'on quitte vraiment la zone.
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setSurvol(false);
      }}
      onDrop={(e) => {
        if (!contientFichiers(e)) return;
        e.preventDefault();
        setSurvol(false);
        if (plein) { toast.info(`Déjà ${MAX_PHOTOS_PARTAGE} photos de partage.`); return; }
        ajouter(e.dataTransfer.files);
      }}
    >
      <div className="np-partage__tete">
        <span className="np-partage__titre">Photos pour le catalogue de partage</span>
        <span className="np-partage__compte">{fichiers.length}/{MAX_PHOTOS_PARTAGE}</span>
      </div>
      <p className="np-partage__aide">
        Les photos « mannequin » envoyées aux clientes. Glissez-les ici, ou choisissez-les.
      </p>

      {fichiers.length > 0 && (
        <div className="np-partage__vignettes">
          {fichiers.map((f, i) => (
            <div className="np-partage__vignette" key={`${f.name}-${f.size}-${i}`}>
              {apercus[i] && <img src={apercus[i]} alt={f.name} />}
              <button
                type="button" className="np-partage__retirer"
                onClick={() => onChanger(fichiers.filter((_, k) => k !== i))}
                aria-label={`Retirer ${f.name}`}
              >
                ×
              </button>
              {i === 0 && <span className="np-partage__badge">1<sup>re</sup></span>}
            </div>
          ))}
        </div>
      )}

      <label className={`np-partage__choisir ${plein ? 'is-desactive' : ''}`}>
        <PictureOutlined aria-hidden /> {fichiers.length ? 'Ajouter d’autres photos' : 'Choisir des photos'}
        <input
          type="file" accept="image/*" multiple disabled={plein}
          onChange={(e) => { ajouter(e.target.files); e.target.value = ''; }}
        />
      </label>
      {survol && !plein && <div className="np-partage__invite">Déposez vos photos ici</div>}
    </div>
  );
}

/*
 * PHOTO D'UN COLORIS — vignette cliquable dans la ligne de la matrice.
 *
 * Un <input type="file"> nu affiche « Aucun fichier sélectionné » et le nom du
 * fichier : illisible dans une cellule de tableau, et surtout inutile — ce que
 * l'on veut voir, c'est la paire. On montre donc l'aperçu, et l'input reste
 * caché derrière le <label> (qui l'active au clic ET au clavier, contrairement
 * à un <div onClick>).
 *
 * L'URL d'objet est révoquée au démontage : sans cela, changer dix fois de photo
 * fuit dix images en mémoire — sur un téléphone, dans un dépôt, cela se sent.
 */
function PhotoColoris({ fichier, replie, onChoisir, libelle }) {
  const [apercu, setApercu] = useState(null);
  const [survol, setSurvol] = useState(false);
  useEffect(() => {
    if (!fichier) { setApercu(null); return undefined; }
    const url = URL.createObjectURL(fichier);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [fichier]);

  // On peut aussi DÉPOSER une image sur la vignette : c'est le geste naturel
  // quand les photos sont déjà ouvertes dans un dossier à côté.
  const contientFichiers = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  return (
    <div className="np-photo-coloris">
      <label
        className={`np-photo-coloris__cible ${survol ? 'is-survol' : ''}`}
        title={`Photo — ${libelle}`}
        onDragOver={(e) => {
          if (!contientFichiers(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setSurvol(true);
        }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSurvol(false); }}
        onDrop={(e) => {
          if (!contientFichiers(e)) return;
          e.preventDefault();
          setSurvol(false);
          // Une seule photo par coloris : on prend la première image déposée.
          const img = Array.from(e.dataTransfer.files || []).find((f) => /^image\//.test(f.type || ''));
          if (img) onChoisir(img);
          else toast.info('Seules les images sont acceptées.');
        }}
      >
        {apercu
          ? <img src={apercu} alt={`Coloris ${libelle}`} className="np-photo-coloris__vue" />
          : (
            <span className="np-photo-coloris__vide">
              <CameraOutlined aria-hidden />
              <span className="np-photo-coloris__mot">{replie ? 'modèle' : 'photo'}</span>
            </span>
          )}
        <input
          type="file" accept="image/*" className="np-photo-coloris__input"
          onChange={(e) => onChoisir(e.target.files?.[0] || null)}
        />
      </label>
      {fichier && (
        <button
          type="button" className="np-photo-coloris__retirer"
          onClick={() => onChoisir(null)}
          aria-label={`Retirer la photo — ${libelle}`}
        >
          Retirer
        </button>
      )}
    </div>
  );
}

/*
 * ASSISTANT « NOUVEAU MODÈLE » (09/08/2026).
 *
 * Remplace l'enchaînement « Créer un produit » → « Entrée de stock » → « Coûts
 * d'achat » : trois écrans, un code inventé à la main, et un appel par pointure
 * dont une partie pouvait échouer.
 *
 * Ce que l'utilisateur ne saisit PLUS, parce que le serveur s'en charge :
 * les codes (`code_variante`, `code_variant`), les références, la date d'entrée,
 * la date de mise à jour, l'auteur. Tout cela était demandé et n'aurait jamais
 * dû l'être.
 *
 * Les trois étapes vivent dans la MÊME page : changer d'étape ne change pas de
 * route et ne perd rien. C'est ce qui permet de revenir corriger une quantité
 * sans tout recommencer.
 */
export default function NouveauProduit() {
  const navigate = useNavigate();

  const [etape, setEtape] = useState(1);
  const [produit, setProduit] = useState({
    nom_produit: '', id_categorie: null, id_marque: null,
    id_matiere: null, id_cible: null, id_hauteur_talon: null, prix: '', date_entrant: '',
  });
  const [couleursChoisies, setCouleursChoisies] = useState([]);
  const [taillesChoisies, setTaillesChoisies] = useState([]);
  const [matrice, setMatrice] = useState({});
  // DEUX NIVEAUX DE PHOTO.
  //
  // `photo` est celle du MODÈLE : le repli, utilisé pour tout coloris qui n'a
  // pas la sienne. `photosColoris` en porte une par couleur — parce qu'une paire
  // noire et une paire beige ne se ressemblent pas, et que la carte du catalogue
  // est ce que la cliente regarde d'abord.
  //
  // Ce sont des objets File, jamais des URL : ils ne passent donc PAS par
  // `construireCorps` (JSON.stringify d'un File rend « {} »). Ils voyagent dans
  // le FormData, l'ordre d'envoi portant l'appariement (voir `envoyer`).
  const [photo, setPhoto] = useState(null);
  const [photosColoris, setPhotosColoris] = useState({});
  // Photos « mannequin » du catalogue de partage. Elles ne partent PAS avec la
  // création : leur route veut un id_produit, qui n'existe qu'après. On les
  // téléverse juste après, une par une (voir `envoyer`).
  const [photosPartage, setPhotosPartage] = useState([]);
  const { can } = usePermissions();
  const peutPartager = can('photos_partage.gerer');
  // LES CHAMPS DE FACTURE NE S'AFFICHENT QU'À QUI PEUT LES ENVOYER (12/08/2026).
  // Côté serveur, un montant fait basculer la garde vers les rôles « coûts ».
  // Les montrer à une secrétaire l'aurait laissée remplir tout l'assistant pour
  // se voir refuser à la validation — après le travail, pas avant.
  const utilisateur = useSelector((s) => s.user?.currentUser);
  const peutSaisirCout = canSeeFinance(utilisateur);
  const [pays, setPays] = useState(null);
  const [seuilPrix, setSeuilPrix] = useState(SEUIL_PRIX_DEFAUT);

  /*
   * RETIRER UNE COULEUR EFFACE SA SAISIE (17/08/2026).
   *
   * `setCouleursChoisies` était branché nu : la couleur disparaissait de
   * l'écran, mais ses quantités et sa photo restaient en mémoire. On pouvait
   * ainsi créer du stock pour une couleur que plus personne ne voyait — et
   * l'écran allait jusqu'à faire confirmer « aucune paire ».
   *
   * Deux protections, et les deux comptent : ce nettoyage rend l'état de
   * l'écran honnête, et `construireCorps` refiltre à l'envoi — parce qu'un
   * état peut toujours diverger, et que le stock ne se rattrape pas.
   */
  const retirerOuAjouterCouleurs = (choix) => {
    const gardees = new Set((choix || []).map(Number));
    setMatrice((m) => Object.fromEntries(
      Object.entries(m).filter(([idC]) => gardees.has(Number(idC))),
    ));
    setPhotosColoris((ph) => Object.fromEntries(
      Object.entries(ph).filter(([idC]) => gardees.has(Number(idC))),
    ));
    setCouleursChoisies(choix || []);
  };
  const [arrivage, setArrivage] = useState({ reference: '', commentaire: '', montant_marchandise: '', montant_frais: '' });

  const [refs, setRefs] = useState({ categories: [], marques: [], matieres: [], cibles: [], couleurs: [], tailles: [], pays: [], talons: [] });
  // Mélange de systèmes de pointure : signalé, jamais bloqué.
  const melangeSystemes = useMemo(
    () => systemesPointureMelanges(taillesChoisies, refs.tailles),
    [taillesChoisies, refs.tailles],
  );
  /*
   * La catégorie choisie demande-t-elle une hauteur de talon ?
   *
   * `avec_talon` vient du serveur. FAIL-OPEN sur son absence : une API plus
   * ancienne ne renvoie pas la colonne, et il vaut mieux poser une question de
   * trop (qui se décoche) que d'escamoter le critère sans le dire — c'est
   * exactement ce qui l'a laissé vide pendant des semaines.
   */
  const talonDemande = useMemo(() => {
    if (!produit.id_categorie || !(refs.talons || []).length) return false;
    const cat = (refs.categories || []).find((c) => c.id_categorie === produit.id_categorie);
    return !cat || cat.avec_talon === undefined || Number(cat.avec_talon) === 1;
  }, [produit.id_categorie, refs.categories, refs.talons]);

  /*
   * LES CRITÈRES DU MODÈLE (ouverture, maintien, bout, type de talon…).
   *
   * Chargés PAR CATÉGORIE : c'est le serveur qui sait lesquels poser, parce que
   * la propriétaire les règle depuis les Paramètres. L'écran n'a pas d'avis —
   * figer la liste ici la ferait diverger le jour où elle en ajoute un.
   */
  const [attributs, setAttributs] = useState([]);
  const [valeursAttr, setValeursAttr] = useState({});

  useEffect(() => {
    if (!produit.id_categorie) { setAttributs([]); return undefined; }
    let vivant = true;
    axios.get(`${DOMAIN}/api/produit/attributs/categorie/${produit.id_categorie}`)
      .then(({ data }) => { if (vivant) setAttributs(Array.isArray(data && data.attributs) ? data.attributs : []); })
      // Non bloquant : sans critères, la création reste possible exactement
      // comme avant. Un champ absent vaut mieux qu'un écran cassé.
      .catch(() => { if (vivant) setAttributs([]); });
    return () => { vivant = false; };
  }, [produit.id_categorie]);

  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  /*
   * LA CLÉ DE CETTE SAISIE — stable tant qu'on n'a pas réussi.
   *
   * `useRef` et non `useState` : la clé ne doit RIEN redessiner, et surtout
   * elle doit être lue au moment de l'envoi, pas à celui du rendu. Un
   * `useState` mal synchronisé enverrait l'ancienne valeur.
   *
   * Elle est renouvelée UNIQUEMENT après une création réussie. Surtout pas
   * entre deux tentatives : un renvoi après un réseau lent porterait alors une
   * clé neuve et créerait le jumeau qu'on cherche à éviter.
   */
  const cleCreation = useRef(nouvelleCleCreation());
  const [succes, setSucces] = useState(null);
  const [similaires, setSimilaires] = useState([]);
  // Une panne de référentiel se DIT, au lieu de se déguiser en champ vide.
  const [erreurRef, setErreurRef] = useState(null);

  // ---- Référentiels ------------------------------------------------------------
  // `essai` sert au bouton « Réessayer » : l'incrémenter relance le chargement.
  // Sans lui, une panne réseau laissait l'écran définitivement muet — il fallait
  // recharger la page entière.
  const [essai, setEssai] = useState(0);
  useEffect(() => {
    let vivant = true;
    (async () => {
      // UNE PANNE N'EST PAS UNE ABSENCE DE DONNÉES (10/08/2026).
      //
      // Ces sept listes sont OBLIGATOIRES : sans les couleurs ou les pointures,
      // l'écran ne peut rien enregistrer. Elles étaient chargées par un `get`
      // qui avalait toute erreur et rendait [] — donc un champ muet, sans le
      // moindre message. C'est exactement ainsi que l'assistant est resté
      // inutilisable pendant un jour quand la route des pointures manquait.
      const { listes, erreur: erreurRef } = await chargerReferentiels([
        { cle: 'categories', quoi: 'les catégories', promesse: axios.get(`${DOMAIN}/api/produit/categorie`) },
        { cle: 'marques', quoi: 'les marques', promesse: axios.get(`${DOMAIN}/api/produit/marque`) },
        { cle: 'matieres', quoi: 'les matières', promesse: axios.get(`${DOMAIN}/api/produit/matiere`) },
        { cle: 'cibles', quoi: 'les cibles', promesse: axios.get(`${DOMAIN}/api/produit/cible`) },
        { cle: 'couleurs', quoi: 'les couleurs', promesse: axios.get(`${DOMAIN}/api/produit/couleur`) },
        { cle: 'tailles', quoi: 'les pointures', promesse: axios.get(`${DOMAIN}/api/produit/taille`) },
        { cle: 'pays', quoi: 'les pays de pointure', promesse: axios.get(`${DOMAIN}/api/produit/pays`) },
      ]);
      if (!vivant) return;

      // Le seuil d'alerte sur le prix. Hors du groupe ci-dessus : son absence ne
      // doit RIEN empêcher — l'écran retombe alors sur son propre défaut.
      axios.get(`${DOMAIN}/api/produit/catalogue/reglages`)
        .then(({ data }) => { if (vivant && Number.isFinite(Number(data?.prix_alerte))) setSeuilPrix(Number(data.prix_alerte)); })
        .catch(() => {});

      // REPLI SUR L'ANCIENNE ROUTE. `/api/produit/taille` n'a existé qu'à
      // partir du 10/08 : une APK récente peut se trouver devant une API plus
      // ancienne. `/tailleAll` est plus pauvre (elle ne nomme pas le système de
      // pointure) mais elle permet de saisir.
      let pointures = listes.tailles;
      let erreur = erreurRef;
      if (!pointures.length) {
        const repli = await chargerListe(axios.get(`${DOMAIN}/api/produit/tailleAll`), 'les pointures');
        if (!vivant) return;
        pointures = repli.liste;
        // Le repli a marché : la panne n'en est plus une, on retire le message.
        if (pointures.length && erreur) erreur = null;
      }

      /*
       * LES NIVEAUX DE TALON.
       *
       * Ils étaient mis à `[]` EN DUR : le champ n'apparaissait donc nulle part,
       * et 0 modèle sur 638 portait une hauteur (mesuré le 18/08/2026). Le
       * critère, l'écran de qualification et la route existaient pourtant depuis
       * des semaines — on ne remplit pas ce qu'on ne voit pas.
       *
       * Hors du groupe bloquant : leur absence ne doit RIEN empêcher. Sans eux,
       * le champ ne s'affiche pas et la création reste possible, exactement
       * comme avant.
       */
      let niveauxTalon = [];
      try {
        const { data } = await axios.get(`${DOMAIN}/api/produit/hauteur-talon/niveaux`);
        niveauxTalon = Array.isArray(data && data.niveaux) ? data.niveaux : [];
      } catch (_) { niveauxTalon = []; }

      setErreurRef(erreur);
      setRefs({
        categories: listes.categories, marques: listes.marques, matieres: listes.matieres,
        cibles: listes.cibles, couleurs: listes.couleurs, tailles: pointures,
        pays: listes.pays, talons: niveauxTalon,
      });
      setChargement(false);
    })();
    return () => { vivant = false; };
  }, [essai]);

  // ---- Brouillon ---------------------------------------------------------------
  //
  // Un arrivage se saisit debout dans le dépôt, sur un téléphone. Fermer l'onglet
  // par erreur ne doit pas coûter dix minutes de ressaisie.
  const brouillonPropose = useRef(false);
  useEffect(() => {
    if (brouillonPropose.current) return;
    brouillonPropose.current = true;
    /*
     * L'ANCIENNE CLÉ PARTAGÉE EST SUPPRIMÉE, PAS ADOPTÉE.
     *
     * On ne peut pas savoir à qui appartenait ce brouillon : l'adopter
     * reviendrait à donner la saisie de l'une à l'autre, exactement le défaut
     * qu'on corrige. Perdre au pire un formulaire en cours vaut mieux que
     * présenter le travail de quelqu'un d'autre comme le sien.
     */
    window.localStorage.removeItem(CLE_BROUILLON_LEGACY);
    const d = lireBrouillon(window.localStorage.getItem(cleBrouillon(utilisateur?.id)));
    if (!brouillonUtile(d)) return;
    Modal.confirm({
      title: 'Reprendre votre saisie ?',
      content: `Un modèle en cours de création a été retrouvé${d.produit?.nom_produit ? ` : « ${d.produit.nom_produit} »` : ''}.`,
      okText: 'Reprendre', cancelText: 'Repartir de zéro',
      onOk: () => {
        setProduit((p) => ({ ...p, ...(d.produit || {}) }));
        setMatrice(d.matrice || {});
        setCouleursChoisies(d.couleursChoisies || []);
        setTaillesChoisies([...new Set(lignesDeLaMatrice(d.matrice || {}).map((l) => l.id_taille))]);
        // On ne réinjecte JAMAIS de montant à qui ne peut pas les saisir : les
        // champs sont masqués, elle ne pourrait donc ni les voir ni les effacer,
        // et la validation serait refusée sans qu'elle comprenne pourquoi.
        // Le brouillon peut venir d'un autre compte sur le même navigateur, ou
        // d'avant cette règle.
        const arrivageRepris = { ...(d.arrivage || {}) };
        if (!peutSaisirCout) {
          delete arrivageRepris.montant_marchandise;
          delete arrivageRepris.montant_frais;
        }
        setArrivage((a) => ({ ...a, ...arrivageRepris }));
        setEtape(d.etape || 1);
      },
      onCancel: () => window.localStorage.removeItem(cleBrouillon(utilisateur?.id)),
    });
    // Volontairement une seule fois, à l'arrivée : `brouillonPropose` garde la
    // modale unique. `peutSaisirCout` dépend du rôle, qui ne change pas en
    // cours d'écran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peutSaisirCout]);

  useEffect(() => {
    if (succes) return;   // plus rien à sauver une fois créé
    window.localStorage.setItem(cleBrouillon(utilisateur?.id), brouillonDepuis({ produit, matrice, couleursChoisies, arrivage, etape }));
  }, [produit, matrice, couleursChoisies, arrivage, etape, succes]);

  // Garde de navigation : une fermeture d'onglet en pleine saisie prévient.
  useEffect(() => {
    const garde = (e) => {
      if (succes || (!String(produit.nom_produit || '').trim() && !totalPaires(matrice))) return undefined;
      e.preventDefault(); e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', garde);
    return () => window.removeEventListener('beforeunload', garde);
  }, [produit.nom_produit, matrice, succes]);

  // ---- Recherche de modèles semblables, pendant la saisie ----------------------
  //
  // Suggestion VISUELLE, jamais un blocage : deux modèles distincts peuvent
  // porter un nom proche. Le seul refus dur est celui du serveur, sur un doublon
  // créé à l'instant.
  useEffect(() => {
    const nom = String(produit.nom_produit || '').trim();
    if (nom.length < 2) { setSimilaires([]); return undefined; }
    let vivant = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock?q=${encodeURIComponent(nom)}&limit=40`);
        if (!vivant) return;
        const parModele = new Map();
        (data?.variantes || []).forEach((v) => {
          if (!parModele.has(v.id_produit)) parModele.set(v.id_produit, v);
        });
        setSimilaires([...parModele.values()].slice(0, 6));
      } catch (e) { /* la suggestion est un confort : son échec est muet */ }
    }, 350);
    return () => { vivant = false; clearTimeout(t); };
  }, [produit.nom_produit]);

  // ---- Matrice -----------------------------------------------------------------
  const majCellule = useCallback((idCouleur, idTaille, valeur) => {
    setMatrice((m) => {
      const n = { ...m, [idCouleur]: { ...(m[idCouleur] || {}) } };
      if (valeur === '' || valeur === null || Number(valeur) <= 0) delete n[idCouleur][idTaille];
      else n[idCouleur][idTaille] = Number(valeur);
      if (!Object.keys(n[idCouleur]).length) delete n[idCouleur];
      return n;
    });
  }, []);

  // ── LES POINTURES ─────────────────────────────────────────────────────────
  //
  // 48 pointures cohabitent sur 4 systèmes, et 19 portent le MÊME libellé
  // (« P35 » existe en européen ET en CLA). La boutique travaille en européen —
  // 1562 variantes sur 1563 — et 35 pointures n'ont jamais servi.
  //
  // On met donc en avant CELLES QUI SERVENT, et on range les autres derrière un
  // repli, en nommant leur système. Le critère est l'usage réel renvoyé par le
  // serveur, pas une liste de pointures écrite en dur ici : le jour où la
  // boutique vend en anglais, l'écran suit tout seul.
  const [autresPointures, setAutresPointures] = useState(false);
  const { courantes: pointuresCourantes, autres: pointuresAutres } = useMemo(
    () => grouperPointures(refs.tailles),
    [refs.tailles],
  );

  // Une frappe sur une pastille ajoute ou retire la colonne. En la retirant, on
  // efface AUSSI les quantités de cette pointure : laisser des nombres dans une
  // colonne invisible créerait des variantes qu'on ne voit pas.
  const basculerTaille = useCallback((idTaille) => {
    setTaillesChoisies((liste) => {
      if (!liste.includes(idTaille)) return [...liste, idTaille];
      setMatrice((m) => {
        const n = { ...m };
        Object.keys(n).forEach((c) => {
          if (n[c] && n[c][idTaille] !== undefined) {
            n[c] = { ...n[c] };
            delete n[c][idTaille];
            if (!Object.keys(n[c]).length) delete n[c];
          }
        });
        return n;
      });
      return liste.filter((x) => x !== idTaille);
    });
  }, []);

  // « Une paire par pointure » : le geste le plus fréquent, en une frappe.
  // Mesuré sur les réceptions réelles : 67 % des lignes portent la quantité 1,
  // et 84 % valent 1 ou 2.
  const remplirLigne = useCallback((idCouleur, valeur) => {
    setMatrice((m) => {
      const n = { ...m, [idCouleur]: { ...(m[idCouleur] || {}) } };
      taillesChoisies.forEach((t) => { n[idCouleur][t] = valeur; });
      return n;
    });
  }, [taillesChoisies]);

  // Le récapitulatif ne doit compter que les couleurs retenues, sinon il
  // annonce des paires qui ne seront pas créées — ou l'inverse.
  const matriceVisible = useMemo(() => Object.fromEntries(
    Object.entries(matrice).filter(([idC]) => couleursChoisies.map(Number).includes(Number(idC))),
  ), [matrice, couleursChoisies]);
  const recap = useMemo(() => recapitulatif({ matrice: matriceVisible, produit, arrivage }), [matriceVisible, produit, arrivage]);
  const nomCouleur = useCallback((id) => refs.couleurs.find((c) => c.id_couleur === id)?.description || `Couleur ${id}`, [refs.couleurs]);
  const nomTaille = useCallback((id) => refs.tailles.find((t) => t.id_taille === id)?.taille ?? id, [refs.tailles]);

  // Les colonnes suivent l'ordre des POINTURES, pas celui des frappes : une
  // colonne 41 posée entre 36 et 38 se lit mal quand on vérifie un arrivage.
  const taillesTriees = useMemo(
    () => taillesChoisies.slice().sort((a, b) => Number(nomTaille(a)) - Number(nomTaille(b))),
    [taillesChoisies, nomTaille],
  );

  // ---- Envoi -------------------------------------------------------------------
  /*
   * UNE FICHE SANS MARCHANDISE SE DÉCIDE, ELLE NE S'OBTIENT PAS PAR OUBLI.
   *
   * Signalé en production le 17/08/2026 : après une réception, « un article est
   * apparu sans pointure et sans photo ». C'était le produit 658 — créé sans
   * aucune couleur, donc sans variante, donc sans pointure ni photo (les deux
   * vivent sur la variante).
   *
   * La propriétaire avait demandé de GARDER la possibilité de créer une fiche
   * sans marchandise, et j'avais assoupli la validation pour ça. Mais assouplir
   * a fait de ce cas voulu le résultat SILENCIEUX d'une grille non remplie :
   * Nobel faisait une réception, elle voulait des paires.
   *
   * On garde donc la capacité, en la nommant : rien ne part sans un « oui »
   * explicite qui dit la conséquence.
   */
  const envoyer = () => {
    if (envoi) return;

    // 1. UNE PHOTO SANS COULEUR SERAIT PERDUE — on refuse, on ne la jette pas.
    //
    // La photo se range sur la couleur (`varianteproduit.img`) : sans couleur,
    // elle était téléversée, ses vignettes fabriquées, puis rattachée à rien.
    // Le serveur refuse également (une vieille APK ne contourne pas la règle) ;
    // ici on l'attrape avant l'envoi, pour ne pas faire attendre un refus qu'on
    // peut prédire.
    if (!couleursChoisies.length && (photo || Object.values(photosColoris).some(Boolean))) {
      Modal.warning({
        title: 'Cette photo ne pourrait pas être conservée',
        okText: 'J’ai compris',
        content: (
          <div>
            <p style={{ margin: '0 0 8px' }}>
              Une photo se range sur une <b>couleur</b>. Comme aucune couleur
              n’est choisie, elle ne serait rattachée à rien et vous la perdriez.
            </p>
            <p style={{ margin: 0 }}>
              Ajoutez une couleur avec ses pointures à l’étape 2, ou retirez la
              photo pour créer une fiche seule.
            </p>
          </div>
        ),
      });
      return;
    }

    // 2. UN PRIX ANORMALEMENT HAUT : on demande, on ne bloque jamais.
    const prixSaisi = Number(produit.prix);
    if (seuilPrix > 0 && Number.isFinite(prixSaisi) && prixSaisi > seuilPrix) {
      Modal.confirm({
        title: `${money(prixSaisi)} — est-ce bien le prix ?`,
        okText: 'Oui, ce prix est correct',
        cancelText: 'Corriger le prix',
        content: (
          <p style={{ margin: 0 }}>
            Ce prix dépasse {money(seuilPrix)}, le seuil d’alerte de la boutique.
            Vérifiez qu’il ne manque pas — ou qu’il ne s’ajoute pas — un zéro.
          </p>
        ),
        onOk: () => suiteValidation(),
      });
      return;
    }

    suiteValidation();
  };

  // Ce qui suit le contrôle du prix : la question de la fiche sans marchandise.
  const suiteValidation = () => {
    if (confirmationSansMarchandiseRequise(couleursChoisies)) {
      Modal.confirm({
        title: 'Créer cette fiche SANS marchandise ?',
        okText: 'Oui, fiche seule',
        cancelText: 'Non, ajouter des pointures',
        content: (
          <div>
            <p style={{ margin: '0 0 8px' }}>
              Aucune couleur n’est choisie. Le modèle sera créé, mais
              <b> sans aucune paire en stock, sans pointure et sans photo</b> —
              c’est ainsi qu’il apparaîtra dans le catalogue.
            </p>
            <p style={{ margin: 0 }}>
              Si vous enregistrez un arrivage, revenez à l’étape 2 et choisissez
              au moins une couleur avec ses pointures.
            </p>
          </div>
        ),
        onOk: () => envoyerVraiment(),
      });
      return;
    }
    envoyerVraiment();
  };

  const envoyerVraiment = async () => {
    if (envoi) return;               // anti-double-clic côté écran…
    setEnvoi(true);                  // …le serveur a sa propre garde (409).
    try {
      // `couleursChoisies` fait foi : une couleur retirée du sélecteur ne doit
      // pas créer de stock, même si la matrice en garde encore la trace.
      const corps = construireCorps({
        produit, matrice, images: {}, pays, arrivage, couleursChoisies,
        cleCreation: cleCreation.current,
      });

      // On n'envoie QUE les photos des couleurs réellement gardées : décocher
      // une couleur après avoir choisi sa photo ne doit pas téléverser un
      // fichier pour rien.
      const couleursRetenues = new Set(corps.coloris.map((c) => c.id_couleur));
      const aEnvoyer = Object.entries(photosColoris)
        .filter(([id, f]) => f && couleursRetenues.has(Number(id)))
        .map(([id, f]) => ({ id: Number(id), fichier: f }));

      let reponse;
      if (photo || aEnvoyer.length) {
        // multipart : le serveur fabrique aussi les vignettes WebP.
        const fd = new FormData();
        Object.entries(corps).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          fd.append(k, k === 'coloris' ? JSON.stringify(v) : v);
        });

        // APPARIEMENT PHOTO ↔ COLORIS. multer impose des noms de champ fixes :
        // impossible de nommer un champ « photo_couleur_7 ». On envoie donc tous
        // les fichiers sous `photos` et, DANS LE MÊME ORDRE, la liste des
        // couleurs sous `photos_couleurs`. L'ordre des append() d'un même champ
        // est garanti par la spécification FormData.
        //
        // La photo du MODÈLE part en tête, marquée 0 (aucune couleur ne porte
        // cet id) : le serveur prend le premier fichier comme repli.
        const couleursDesPhotos = [];
        if (photo) { fd.append('photos', photo); couleursDesPhotos.push(0); }
        aEnvoyer.forEach(({ id, fichier }) => { fd.append('photos', fichier); couleursDesPhotos.push(id); });
        fd.append('photos_couleurs', JSON.stringify(couleursDesPhotos));

        reponse = await axios.post(`${DOMAIN}/api/produit/catalogue/produits/avec-arrivage`, fd);
      } else {
        reponse = await axios.post(`${DOMAIN}/api/produit/catalogue/produits/avec-arrivage`, corps);
      }
      const { data } = reponse;
      window.localStorage.removeItem(cleBrouillon(utilisateur?.id));

      // PHOTOS DE PARTAGE — après coup, et jamais bloquantes.
      //
      // Leur route est /api/photos-partage/:idProduit : elle a besoin d'un id
      // que la création vient tout juste de rendre. Une par une, en série (le
      // serveur compte les photos existantes à chaque insertion pour appliquer
      // son maximum : en parallèle, deux requêtes liraient le même compte).
      //
      // Le modèle EST créé à ce stade. Une photo qui échoue ne doit donc pas
      // ressembler à un échec de création — on le dit, et on continue : les
      // photos se rattrapent depuis « Photos à partager ».
      /*
       * LES CRITÈRES, POSÉS APRÈS COUP — et jamais bloquants.
       *
       * Leur route a besoin de l'id que la création vient de rendre. Le modèle
       * EXISTE à ce stade : un critère qui échoue ne doit donc pas ressembler à
       * un échec de création. On le dit, et on continue — ils se rattrapent
       * depuis l'écran de qualification.
       */
      const criteresChoisis = Object.fromEntries(
        Object.entries(valeursAttr).filter(([, v]) => v !== null && v !== undefined && v !== ''),
      );
      if (Object.keys(criteresChoisis).length) {
        try {
          await axios.put(`${DOMAIN}/api/produit/attributs/produit/${data.id_produit}`, { valeurs: criteresChoisis });
        } catch (err) {
          toast.warn('Modèle créé, mais ses critères n’ont pas pu être enregistrés — reprenez-les depuis l’écran de qualification.');
        }
      }

      let partageEchouees = 0;
      for (const f of photosPartage) {
        const fdp = new FormData();
        fdp.append('photo', f);
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${DOMAIN}/api/photos-partage/${data.id_produit}`, fdp);
        } catch (err) { partageEchouees += 1; }
      }
      if (partageEchouees) {
        toast.warn(`Modèle créé. ${partageEchouees} photo(s) de partage n’ont pas pu être envoyées — reprenez-les depuis « Photos à partager ».`);
      }

      // Le modèle EXISTE : la saisie suivante doit porter une clé neuve, sinon
      // elle serait prise pour un renvoi de celle-ci et rejouerait sa réponse.
      cleCreation.current = nouvelleCleCreation();
      setSucces({ ...data, photos_partage: photosPartage.length - partageEchouees });
    } catch (e) {
      const r = e.response;
      if (r?.status === 409) {
        // Le serveur a reconnu un modèle créé à l'instant : ce n'est pas une
        // erreur de l'utilisateur, c'est un double envoi qu'on lui explique.
        Modal.info({
          title: 'Ce modèle vient d’être créé',
          content: r.data?.error || 'Un modèle identique existe déjà.',
          okText: 'Voir le catalogue',
          onOk: () => navigate('/varianteProduit'),
        });
      } else {
        toast.error(r?.data?.error || 'Création impossible.');
      }
    } finally { setEnvoi(false); }
  };

  // ---- Écran de succès ---------------------------------------------------------
  if (succes) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Catalogue" title="Modèle créé" subtitle={succes.nom_produit} />
        <div className="np-succes">
          <p className="np-succes__ref">
            Référence <b className="num">{succes.code_produit}</b>
          </p>
          <NdoeStats>
            <NdoeStatCard label="Paires reçues" value={succes.paires} gold />
            <NdoeStatCard label="Variantes créées" value={succes.variantes?.length ?? 0} />
            <NdoeStatCard label="Prix de vente" value={money(succes.prix)} />
            {succes.photos_partage > 0 && (
              <NdoeStatCard label="Photos de partage" value={succes.photos_partage} />
            )}
          </NdoeStats>
          <div className="np-succes__skus">
            {(succes.variantes || []).map((v) => (
              <Tag key={v.id_varianteProduit} className="num">{v.code_article}</Tag>
            ))}
          </div>
          <div className="np-actions">
            <NdoeActionButton variant="secondary" onClick={() => window.location.reload()}>
              Créer un autre modèle
            </NdoeActionButton>
            <NdoeActionButton variant="primary" onClick={() => navigate('/varianteProduit')}>
              Voir le catalogue
            </NdoeActionButton>
          </div>
        </div>
      </NdoePage>
    );
  }

  if (chargement) return <NdoePage><NdoeLoading rows={6} /></NdoePage>;

  // UNE PANNE SE DIT. Sans les listes de référence, la saisie serait
  // incomplète : on ne laisse pas remplir un formulaire qu'on ne pourra pas
  // enregistrer, et surtout on ne fait pas croire que « il n'y a rien ».
  if (erreurRef) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Catalogue" title="Nouveau modèle" />
        <NdoeError
          title="Les listes n’ont pas pu être chargées"
          text={erreurRef}
          onRetry={() => { setChargement(true); setEssai((n) => n + 1); }}
        />
      </NdoePage>
    );
  }

  const raison1 = identiteRaison(produit);
  const raison2 = matriceRaison(matrice, couleursChoisies);
  const raison3 = prixRaison(produit.prix);

  return (
    <NdoePage className="np-page">
      <NdoePageHeader
        eyebrow="Catalogue"
        title="Nouveau modèle"
        subtitle="Le modèle, ses couleurs et pointures, son prix — et le premier arrivage, en une seule fois."
      />

      {/* ÉTAPE 1 — l'identité */}
      <NdoeFormSection step={1} title="Le modèle" collapsible={false}>
        <div className="np-grille">
          <label className="np-champ np-champ--large">
            <span>Nom du modèle *</span>
            {/* SAISIE PRÉDICTIVE (demande du propriétaire, 09/08).
                Le nom se tape, et ce qui existe déjà apparaît SOUS LE CURSEUR —
                pas dans un bloc qu'on lit après coup. « Cela permet de savoir
                dans quelle direction on va » : si le modèle existe, on le voit
                avant d'avoir fini de le nommer.
                Choisir une suggestion ne remplit RIEN : ce serait créer un
                doublon de nom. Elle informe, et laisse décider. */}
            <AutoComplete
              value={produit.nom_produit}
              onChange={(v) => setProduit((p) => ({ ...p, nom_produit: v }))}
              options={similaires.map((s) => ({
                value: s.nom_produit,
                label: (
                  <span className="np-suggestion">
                    <NdoeImage src={s.img} alt={s.nom_produit} size="sm" preview={false} />
                    <span>
                      <b>{s.nom_produit}</b>
                      <small className="num">{s.code_produit} · {s.marque || ''} · {s.disponible ?? 0} en stock</small>
                    </span>
                  </span>
                ),
              }))}
              // On n'écrase jamais la saisie : la suggestion ouvre la fiche
              // existante plutôt que de recopier son nom.
              onSelect={(_, opt) => {
                const s = similaires.find((x) => x.nom_produit === opt.value);
                if (s) window.open(`/varianteProduit?q=${encodeURIComponent(s.code_produit || s.nom_produit)}`, '_blank');
                setProduit((p) => ({ ...p, nom_produit: produit.nom_produit }));
              }}
              popupMatchSelectWidth
            >
              <Input maxLength={190} placeholder="Escarpin daim noir à bride" />
            </AutoComplete>
          </label>
          {/* LA DATE D'ENTRÉE (10/08/2026). Facultative : sans elle, le serveur
              date au jour de la saisie. Elle sert quand on enregistre aujourd'hui
              un arrivage arrivé le mois dernier — les rapports d'ancienneté de
              stock et de rotation s'appuient dessus. */}
          <label className="np-champ">
            <span>Date d’entrée</span>
            <input
              type="date"
              value={produit.date_entrant || ''}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setProduit((p) => ({ ...p, date_entrant: e.target.value }))}
            />
            <small className="np-photo-nom">
              {produit.date_entrant
                ? (dateEntreeRaison(produit.date_entrant) || 'L’arrivage sera daté du jour choisi.')
                : 'Laissez vide pour dater d’aujourd’hui.'}
            </small>
          </label>
          <label className="np-champ np-champ--large">
            <span>Photo du modèle</span>
            <input
              type="file" accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)}
            />
            <small className="np-photo-nom">
              {photo
                ? `${photo.name} — servira aux coloris sans photo propre.`
                : 'Facultatif. Vous pourrez donner sa photo à chaque coloris à l’étape 2.'}
            </small>
          </label>
          {[
            ['id_categorie', 'Catégorie *', refs.categories, 'id_categorie', 'nom_categorie'],
            ['id_marque', 'Marque *', refs.marques, 'id_marque', 'nom'],
            ['id_matiere', 'Matière *', refs.matieres, 'id_matiere', 'nom_matiere'],
            ['id_cible', 'Cible *', refs.cibles, 'id_cible', 'nom_cible'],
          ].map(([cle, libelle, liste, idCol, nomCol]) => (
            <label className="np-champ" key={cle}>
              <span>{libelle}</span>
              <Select
                showSearch optionFilterProp="label" allowClear
                value={produit[cle]} placeholder="Choisir…"
                onChange={(v) => setProduit((p) => ({ ...p, [cle]: v }))}
                options={(liste || []).map((x) => ({ value: x[idCol], label: x[nomCol] }))}
              />
            </label>
          ))}

          {/*
            LA HAUTEUR DE TALON — POSÉE LÀ OÙ ELLE A UN SENS (18/08/2026).

            Demander la hauteur d'une ballerine n'aurait pas de sens : elle est
            plate par définition. C'est la CATÉGORIE qui décide, via son drapeau
            `avec_talon` réglé par la propriétaire — pas une liste figée dans le
            code, qui casserait à la première catégorie ajoutée.

            Le champ n'apparaît qu'une fois la catégorie choisie : avant, la
            question n'a pas d'objet. Et il reste FACULTATIF — un modèle dont on
            ignore le talon doit pouvoir être créé.
          */}
          {/*
            LES CRITÈRES DU MODÈLE — même logique que la hauteur.

            Ils décrivent la chaussure et servent ensuite à la retrouver dans le
            catalogue. TOUS FACULTATIFS : un modèle dont on ignore le bout doit
            pouvoir être créé. On les pose ICI parce que c'est le moment où
            l'information est sous les yeux — la hauteur de talon a mis des
            semaines à ne jamais se remplir, faute d'être demandée au bon moment.
          */}
          {attributs.map((a) => (
            <label className="np-champ" key={a.code}>
              <span>{a.libelle}</span>
              <Select
                allowClear showSearch optionFilterProp="label"
                value={valeursAttr[a.code] ?? null} placeholder="Facultatif"
                onChange={(v) => setValeursAttr((m) => ({ ...m, [a.code]: v ?? null }))}
                options={(a.valeurs || []).map((v) => ({ value: v.id_valeur, label: v.libelle }))}
              />
            </label>
          ))}

          {talonDemande && (
            <label className="np-champ">
              <span>Hauteur de talon</span>
              <Select
                allowClear showSearch optionFilterProp="label"
                value={produit.id_hauteur_talon} placeholder="Facultatif"
                onChange={(v) => setProduit((p) => ({ ...p, id_hauteur_talon: v }))}
                options={(refs.talons || []).map((t) => ({
                  value: t.id_hauteur_talon,
                  label: t.fourchette ? `${t.nom} (${t.fourchette})` : t.nom,
                }))}
              />
            </label>
          )}
        </div>

        {/* Le bloc « modèles semblables » qui vivait ici a été retiré : il
            faisait doublon avec le menu prédictif du champ, qui dit la même
            chose au bon moment — pendant la frappe, pas après. */}
        {similaires.length > 0 && (
          <p className="np-indice">
            <SearchOutlined /> {similaires.length} modèle(s) proche(s) existent — voyez la liste sous le champ.
          </p>
        )}
        {raison1 && <p className="np-raison">{raison1}</p>}
      </NdoeFormSection>

      {/* ÉTAPE 2 — couleurs, pointures, quantités */}
      <NdoeFormSection step={2} title="Couleurs, pointures et quantités" collapsible={false}>
        {/* DIT AVANT, PAS APRÈS. Sans couleur, la fiche partira sans variante —
            donc sans pointure et sans photo dans le catalogue. C'est une
            possibilité voulue, mais elle doit se voir pendant la saisie et non
            se découvrir dans la liste des produits (constat prod 17/08/2026). */}
        {!!melangeSystemes.length && (
          <p className="np-raison np-raison--info">
            Vous mélangez deux systèmes de pointure ({melangeSystemes.join(' et ')}).
            C’est possible, mais le stock sera plus difficile à lire : « P38 »
            ne désignera plus la même chose selon la paire.
          </p>
        )}
        {!couleursChoisies.length && (
          <p className="np-raison np-raison--info">
            Aucune couleur choisie : le modèle sera créé <b>sans paire en stock,
            sans pointure et sans photo</b>. Si vous enregistrez un arrivage,
            choisissez au moins une couleur ci-dessous.
          </p>
        )}
        <div className="np-grille">
          <label className="np-champ np-champ--large">
            <span>Couleurs reçues</span>
            <Select
              mode="multiple" showSearch optionFilterProp="label" allowClear
              value={couleursChoisies} placeholder="Choisir une ou plusieurs couleurs…"
              onChange={retirerOuAjouterCouleurs}
              // Replie par FAMILLE : 55 nuances tenaient dans une seule liste
              // qu'on parcourait au lieu d'y choisir.
              options={optionsCouleursParFamille(refs.couleurs)}
            />
          </label>
          {/* POINTURES EN PASTILLES, ET NON EN LISTE DÉROULANTE (10/08/2026).
              Une liste imposait : ouvrir, faire défiler 48 entrées venues de
              4 systèmes de pointures — dont 19 portent le MÊME libellé —,
              cliquer, refermer. Ici : une frappe par pointure.
              Le tri et le regroupement viennent de l'usage réel (voir plus haut). */}
          <div className="np-champ np-champ--large">
            <span>Pointures reçues</span>
            <div className="np-pointures">
              {pointuresCourantes.map((t) => {
                const active = taillesChoisies.includes(t.id_taille);
                return (
                  <button
                    type="button" key={t.id_taille}
                    className={`np-pointure ${active ? 'is-active' : ''}`}
                    aria-pressed={active}
                    onClick={() => basculerTaille(t.id_taille)}
                  >
                    {t.taille}
                  </button>
                );
              })}
              {!pointuresCourantes.length && <span className="np-pointures__vide">Chargement des pointures…</span>}
            </div>

            {/* Les autres systèmes existent mais n'ont jamais servi (35 des 48).
                On les range derrière un repli, en NOMMANT leur système — sans
                cela, « P8 » (anglais) se confond avec une pointure enfant. */}
            {pointuresAutres.length > 0 && (
              <>
                <button
                  type="button" className="np-pointures__plus"
                  onClick={() => setAutresPointures((v) => !v)}
                >
                  {autresPointures ? '− Masquer' : `+ Autres systèmes (${pointuresAutres.length})`}
                </button>
                {autresPointures && (
                  <div className="np-pointures np-pointures--autres">
                    {pointuresAutres.map((t) => {
                      const active = taillesChoisies.includes(t.id_taille);
                      return (
                        <button
                          type="button" key={t.id_taille}
                          className={`np-pointure ${active ? 'is-active' : ''}`}
                          aria-pressed={active}
                          onClick={() => basculerTaille(t.id_taille)}
                        >
                          {t.taille}<small>{t.code_pays}</small>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* LA MATRICE. C'est le cœur de l'écran : une cellule par couleur ×
            pointure, et on ne crée QUE celles qui portent une quantité. Un zéro
            n'est pas une variante — c'est ce qui évite un catalogue rempli de
            paires fantômes. */}
        {/* Le tableau apparaît dès la PREMIÈRE COULEUR, et non plus seulement
            quand couleurs ET pointures sont choisies. Auparavant, tant qu'aucune
            pointure n'était prise, l'écran ne montrait RIEN — tout en réclamant
            « au moins une quantité reçue » dans un message rouge, sans qu'aucun
            champ de quantité ne soit visible. On ne peut pas deviner un tableau
            qui n'existe pas encore. */}
        {couleursChoisies.length > 0 && taillesChoisies.length === 0 && (
          <p className="np-invite">
            Choisissez une ou plusieurs pointures ci-dessus : le tableau des quantités s’ouvrira ici.
          </p>
        )}
        {couleursChoisies.length > 0 && taillesChoisies.length > 0 && (
          <div className="np-matrice-cadre">
            <table className="np-matrice">
              <thead>
                <tr>
                  <th>Couleur et photo</th>
                  {taillesTriees.map((t) => <th key={t} className="num">P{nomTaille(t)}</th>)}
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {couleursChoisies.map((c) => {
                  const totalLigne = Object.values(matrice[c] || {}).reduce((s, v) => s + Number(v || 0), 0);
                  return (
                    <tr key={c}>
                      <th scope="row">
                        <div className="np-matrice__couleur">
                          <PhotoColoris
                            fichier={photosColoris[c] || null}
                            replie={!photosColoris[c] && !!photo}
                            libelle={nomCouleur(c)}
                            onChoisir={(f) => setPhotosColoris((p) => {
                              const n = { ...p };
                              if (f) n[c] = f; else delete n[c];
                              return n;
                            })}
                          />
                          <span className="np-matrice__nom">{nomCouleur(c)}</span>
                        </div>
                      </th>
                      {taillesTriees.map((t) => (
                        <td key={t}>
                          <InputNumber
                            min={0} max={999} controls={false} size="small"
                            value={matrice[c]?.[t] ?? null}
                            onChange={(v) => majCellule(c, t, v)}
                            // Sans cela, taper 3 dans une case qui porte 1 donne 13 ou 31.
                            onFocus={(e) => e.target.select()}
                            placeholder="0"
                          />
                        </td>
                      ))}
                      <td className="num np-total-ligne">
                        {totalLigne || '—'}
                        <button
                          type="button" className="np-ligne-un"
                          title="Une paire pour chaque pointure de cette ligne"
                          onClick={() => remplirLigne(c, totalLigne ? 0 : 1)}
                        >
                          {totalLigne ? 'vider' : '1 partout'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* Les photos de partage vivent ICI, à la suite des photos de coloris :
            c'est le moment où l'on a les images sous la main. Réservé à qui a
            la permission — sinon le téléversement échouerait en 403 après coup,
            alors que le modèle, lui, serait bien créé. */}
        {peutPartager && (
          <ZonePhotosPartage fichiers={photosPartage} onChanger={setPhotosPartage} />
        )}
        {raison2 && <p className="np-raison">{raison2}</p>}
      </NdoeFormSection>

      {/* ÉTAPE 3 — prix, coût, validation */}
      <NdoeFormSection step={3} title="Prix, coût et validation" collapsible={false}>
        <div className="np-grille">
          <label className="np-champ">
            <span>Prix de vente (USD) *</span>
            <InputNumber
              min={1} controls={false} style={{ width: '100%' }}
              value={produit.prix === '' ? null : produit.prix}
              onChange={(v) => setProduit((p) => ({ ...p, prix: v ?? '' }))}
              placeholder="90"
            />
          </label>
          <label className="np-champ">
            <span>Pays de pointure</span>
            <Select
              allowClear value={pays} onChange={setPays} placeholder="Facultatif"
              options={(refs.pays || []).map((p) => ({ value: p.id_pays, label: p.code_pays || p.nom_pays }))}
            />
          </label>
          {peutSaisirCout && (
            <>
              <label className="np-champ">
                <span>Coût marchandise</span>
                <InputNumber min={0} controls={false} style={{ width: '100%' }}
                  value={arrivage.montant_marchandise === '' ? null : arrivage.montant_marchandise}
                  onChange={(v) => setArrivage((a) => ({ ...a, montant_marchandise: v ?? '' }))} placeholder="Facultatif" />
              </label>
              <label className="np-champ">
                <span>Frais</span>
                <InputNumber min={0} controls={false} style={{ width: '100%' }}
                  value={arrivage.montant_frais === '' ? null : arrivage.montant_frais}
                  onChange={(v) => setArrivage((a) => ({ ...a, montant_frais: v ?? '' }))} placeholder="Facultatif" />
              </label>
            </>
          )}
          <label className="np-champ np-champ--large">
            <span>Référence de facture</span>
            <Input value={arrivage.reference} maxLength={60} placeholder="Facultatif"
              onChange={(e) => setArrivage((a) => ({ ...a, reference: e.target.value }))} />
          </label>
        </div>

        {/* LE RÉCAPITULATIF, avant d'engager. On ne valide pas à l'aveugle une
            écriture qui touche le stock et l'argent. */}
        <div className="np-recap">
          <span><b className="num">{recap.paires}</b> paire(s)</span>
          <span><b className="num">{recap.variantes}</b> variante(s) sur <b className="num">{recap.couleurs}</b> couleur(s)</span>
          <span>valeur de vente <b className="num">{money(recap.valeurVente)}</b></span>
          {recap.coutTotal > 0 && <span>coût <b className="num">{money(recap.coutTotal)}</b></span>}
          {recap.marge !== null
            ? <span className={recap.marge < 0 ? 'np-marge is-neg' : 'np-marge'}>marge <b className="num">{money(recap.marge)}</b> ({recap.tauxMarge} %)</span>
            : <span className="np-marge np-marge--absente">marge non calculée — aucun coût saisi</span>}
        </div>
        {raison3 && <p className="np-raison">{raison3}</p>}
      </NdoeFormSection>

      <div className="ndoe-sticky-cta">
        <NdoeActionButton variant="secondary" onClick={() => navigate('/varianteProduit')}>Annuler</NdoeActionButton>
        <NdoeActionButton
          variant="primary" loading={envoi}
          disabled={!identiteValide(produit) || !matriceValide(matrice, couleursChoisies)
            || !prixValide(produit.prix) || !!dateEntreeRaison(produit.date_entrant)}
          onClick={envoyer}
        >
          {couleursChoisies.length
            ? "Créer le modèle et enregistrer l'arrivage"
            : 'Créer la fiche seule (sans marchandise)'}
        </NdoeActionButton>
      </div>
      {/* La raison du blocage est dite, pas devinée. */}
      {(raison1 || raison2 || raison3) && (
        <p className="np-raison np-raison--cta">{raison1 || raison2 || raison3}</p>
      )}
    </NdoePage>
  );
}
